import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Input, Pagination, Segmented, Select, message } from 'antd';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowDown,
  DollarSign,
  FileSpreadsheet,
  FileText,
  GraduationCap,
  MapPin,
  Search,
  TrendingUp,
  Users,
} from 'lucide-react';
import { PageHeader } from '@/shared/components';
import { Button } from '@/shared/components/Button';
import { KpiCard } from '@/features/budgets/components/KpiCard';
import { useBudgetReportYearsListQuery } from '@/features/budgets/hooks/useBudgetQueries';
import { useDefaultYear } from '@/features/settings/hooks/useDashboardSettingsQuery';
import {
  useAreasForCitiesQuery,
  useCitiesForGovernoratesQuery,
  useGovernoratesQuery,
} from '@/features/region/hooks/useRegionQueries';
import { useTuitionFeeBenchmarkQuery } from '@/features/reports/hooks/useTuitionFeeBenchmarkQuery';
import { usePickListQuery } from '@/features/reports/hooks/usePickListQuery';
import { PICK_LIST_CODES } from '@/features/reports/api';
import {
  NEAR_REGIONAL_THRESHOLD_PCT,
  statusLabel,
} from '@/features/reports/data/tuitionFeeBenchmarkMock';
import type {
  TuitionFeeBenchmarkFilterParams,
  TuitionFeeBenchmarkMatrixCell,
  TuitionFeeBenchmarkRowFilter,
  TuitionFeeBenchmarkSchoolRow,
  TuitionFeeBenchmarkViewMode,
} from '@/features/reports/data/tuitionFeeBenchmark.types';
import {
  exportTuitionFeeBenchmarkExcel,
  exportTuitionFeeBenchmarkPdf,
  tuitionFeeBenchmarkExportBasename,
} from '@/features/reports/utils/tuitionFeeBenchmarkExports';
import { schoolProfileHref } from '@/app/router/routes';

const TABLE_PAGE_SIZE_OPTIONS = [10, 15, 25, 50] as const;

const ANY_VALUE = '';

type SortKey =
  | 'schoolName'
  | 'studentsInGrade'
  | 'tuitionPerStudentUsd'
  | 'varianceRegionalPct'
  | 'varianceNationalPct';

type SortDir = 'asc' | 'desc';

function withAnyOption(items: { value: string; label: string }[]) {
  return [{ value: ANY_VALUE, label: 'All governorates' }, ...items];
}

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtPct(n: number): string {
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function fmtInt(n: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(n));
}

function abbrevSchoolName(name: string): string {
  return name.length > 28 ? `${name.slice(0, 26)}…` : name;
}

function statusBadgeClass(status: TuitionFeeBenchmarkSchoolRow['benchmarkStatus']): string {
  if (status === 'above') return 'bg-amber-100 text-amber-800 ring-amber-200/80';
  if (status === 'below') return 'bg-emerald-100 text-emerald-800 ring-emerald-200/80';
  return 'bg-slate-100 text-slate-700 ring-slate-200/80';
}

function matrixHeatClass(varianceVsNationalPct: number): string {
  if (varianceVsNationalPct >= 12) return 'bg-rose-100 text-rose-900';
  if (varianceVsNationalPct >= 5) return 'bg-amber-50 text-amber-900';
  if (varianceVsNationalPct <= -12) return 'bg-emerald-100 text-emerald-900';
  if (varianceVsNationalPct <= -5) return 'bg-emerald-50 text-emerald-900';
  return 'bg-white text-slate-800';
}

function BenchmarkChart({
  rows,
  regionalBenchmark,
  nationalBenchmark,
  loading,
}: {
  rows: TuitionFeeBenchmarkSchoolRow[];
  regionalBenchmark: number;
  nationalBenchmark: number;
  loading: boolean;
}) {
  const chartData = useMemo(
    () =>
      [...rows]
        .sort((a, b) => b.tuitionPerStudentUsd - a.tuitionPerStudentUsd)
        .slice(0, 24)
        .map((r) => ({
          schoolId: r.schoolId,
          name: abbrevSchoolName(r.schoolName),
          tuition: r.tuitionPerStudentUsd,
          status: r.benchmarkStatus,
          varianceRegionalPct: r.varianceRegionalPct,
          varianceNationalPct: r.varianceNationalPct,
        })),
    [rows]
  );

  if (loading) {
    return (
      <div className="h-[320px] animate-pulse rounded-xl border border-slate-200 bg-slate-100" aria-hidden />
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center rounded-xl border border-dashed border-slate-200 text-sm text-slate-500">
        No schools to chart.
      </div>
    );
  }

  const barColor = (status: TuitionFeeBenchmarkSchoolRow['benchmarkStatus']) => {
    if (status === 'above') return '#d97706';
    if (status === 'below') return '#059669';
    return '#4f46e5';
  };

  return (
    <div className="h-[min(520px,60vh)] w-full min-w-0">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={(v) => {
              const n = Number(v);
              if (n >= 1000) return `$${Math.round(n / 100) / 10}k`;
              return `$${Math.round(n)}`;
            }}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={120}
            tick={{ fontSize: 10 }}
          />
          <RechartsTooltip
            formatter={(value: number, _name, item) => {
              const payload = item.payload as (typeof chartData)[number];
              return [
                `${fmtUsd(value)} · vs regional ${fmtPct(payload.varianceRegionalPct)} · vs national ${fmtPct(payload.varianceNationalPct)}`,
                'Tuition / student',
              ];
            }}
          />
          <ReferenceLine
            x={regionalBenchmark}
            stroke="#64748b"
            strokeDasharray="5 5"
            strokeWidth={1.5}
            label={{ value: 'Regional', position: 'insideTopRight', fill: '#64748b', fontSize: 10 }}
          />
          <ReferenceLine
            x={nationalBenchmark}
            stroke="#94a3b8"
            strokeDasharray="3 3"
            strokeWidth={1.5}
            label={{ value: 'National', position: 'insideBottomRight', fill: '#94a3b8', fontSize: 10 }}
          />
          <Bar dataKey="tuition" name="Tuition / student" radius={[0, 4, 4, 0]} maxBarSize={18}>
            {chartData.map((entry) => (
              <Cell key={entry.schoolId} fill={barColor(entry.status)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {rows.length > 24 ? (
        <p className="mt-2 text-center text-xs text-slate-500">
          Showing top 24 schools by tuition. Use the table for the full list.
        </p>
      ) : null}
    </div>
  );
}

function RegionalMatrixTable({
  cells,
  selectedGradeId,
  loading,
}: {
  cells: TuitionFeeBenchmarkMatrixCell[];
  selectedGradeId: string;
  loading: boolean;
}) {
  const { regions, grades, cellMap } = useMemo(() => {
    const regionSet = new Map<string, string>();
    const gradeSet = new Map<string, string>();
    const map = new Map<string, TuitionFeeBenchmarkMatrixCell>();

    for (const cell of cells) {
      regionSet.set(cell.regionId, cell.regionName);
      gradeSet.set(cell.gradeProgramId, cell.gradeProgramTitle);
      map.set(`${cell.regionId}|${cell.gradeProgramId}`, cell);
    }

    const gradeList = selectedGradeId
      ? [{ id: selectedGradeId, title: gradeSet.get(selectedGradeId) ?? selectedGradeId }]
      : [...gradeSet.entries()]
          .map(([id, title]) => ({ id, title }))
          .sort((a, b) => a.title.localeCompare(b.title));

    return {
      regions: [...regionSet.entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      grades: gradeList,
      cellMap: map,
    };
  }, [cells, selectedGradeId]);

  if (loading) {
    return (
      <div className="h-48 animate-pulse rounded-xl border border-slate-200 bg-slate-100" aria-hidden />
    );
  }

  if (regions.length === 0 || grades.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center rounded-xl border border-dashed border-slate-200 text-sm text-slate-500">
        No regional matrix data.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200/90 bg-white">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50/90">
            <th className="sticky left-0 z-10 bg-slate-50/95 px-4 py-3 text-left font-semibold text-slate-700">
              Region
            </th>
            {grades.map((g) => (
              <th key={g.id} className="min-w-[7rem] px-3 py-3 text-center font-semibold text-slate-700">
                {g.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {regions.map((region) => (
            <tr key={region.id} className="border-b border-slate-100 last:border-0">
              <td className="sticky left-0 z-10 bg-white px-4 py-2.5 font-medium text-slate-800">
                {region.name}
              </td>
              {grades.map((g) => {
                const cell = cellMap.get(`${region.id}|${g.id}`);
                if (!cell) {
                  return (
                    <td key={g.id} className="px-3 py-2.5 text-center text-slate-400">
                      —
                    </td>
                  );
                }
                return (
                  <td
                    key={g.id}
                    className={`px-3 py-2.5 text-center font-mono tabular-nums text-xs ring-1 ring-inset ring-slate-100 ${matrixHeatClass(cell.varianceVsNationalPct)}`}
                    title={`${cell.schoolsCount} schools · ${fmtInt(cell.studentsCount)} students · vs national ${fmtPct(cell.varianceVsNationalPct)}`}
                  >
                    <div className="font-semibold">{fmtUsd(cell.avgTuitionUsd)}</div>
                    <div className="mt-0.5 text-[10px] opacity-80">{fmtPct(cell.varianceVsNationalPct)}</div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2 rounded-xl border border-slate-200/90 bg-white p-4" aria-hidden>
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="h-10 animate-pulse rounded bg-slate-100" />
      ))}
    </div>
  );
}

export function TuitionFeeBenchmarkPage() {
  const reportYearsQuery = useBudgetReportYearsListQuery();
  const defaultYear = useDefaultYear();

  const [year, setYear] = useState('');
  const [governorateId, setGovernorateId] = useState(ANY_VALUE);
  const [cityId, setCityId] = useState(ANY_VALUE);
  const [areaId, setAreaId] = useState(ANY_VALUE);
  const [programId, setProgramId] = useState('');
  const [viewMode, setViewMode] = useState<TuitionFeeBenchmarkViewMode>('chart');
  const [rowFilter, setRowFilter] = useState<TuitionFeeBenchmarkRowFilter>('all');
  const [tableSearch, setTableSearch] = useState('');
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[1]);
  const [applied, setApplied] = useState<TuitionFeeBenchmarkFilterParams | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('varianceRegionalPct');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    if (!year && defaultYear) setYear(defaultYear);
  }, [defaultYear, year]);

  const programsQuery = usePickListQuery(PICK_LIST_CODES.programs);

  const governorateIds = useMemo(
    () => (governorateId.trim() ? [governorateId.trim()] : []),
    [governorateId]
  );
  const cityIds = useMemo(() => (cityId.trim() ? [cityId.trim()] : []), [cityId]);

  const governoratesQuery = useGovernoratesQuery();
  const citiesQuery = useCitiesForGovernoratesQuery(governorateIds);
  const areasQuery = useAreasForCitiesQuery(cityIds, Boolean(year.trim() && governorateIds.length > 0));

  useEffect(() => {
    if (!governorateId.trim()) {
      setCityId(ANY_VALUE);
      setAreaId(ANY_VALUE);
    }
  }, [governorateId]);

  useEffect(() => {
    if (!cityId.trim()) setAreaId(ANY_VALUE);
  }, [cityId]);

  const governorateOptions = useMemo(() => {
    const items = (governoratesQuery.data ?? []).map((g) => ({
      value: String(g.id),
      label: g.name,
    }));
    return withAnyOption(items);
  }, [governoratesQuery.data]);

  const cityOptions = useMemo(() => {
    const items = (citiesQuery.data ?? []).map((c) => ({
      value: String(c.id),
      label: c.name,
    }));
    return [{ value: ANY_VALUE, label: 'All districts' }, ...items];
  }, [citiesQuery.data]);

  const areaOptions = useMemo(() => {
    const items = (areasQuery.data ?? []).map((a) => ({
      value: String(a.id),
      label: a.name,
    }));
    return [{ value: ANY_VALUE, label: 'All areas' }, ...items];
  }, [areasQuery.data]);

  const programOptions = useMemo(
    () =>
      (programsQuery.data ?? [])
        .filter((o) => !o.disabled && o.value.trim())
        .map((o) => ({ value: o.value, label: o.text })),
    [programsQuery.data]
  );

  const yearOptions = useMemo(() => {
    const raw = reportYearsQuery.data ?? [];
    return [...raw].sort((a, b) => b - a).map((n) => ({ value: String(n), label: String(n) }));
  }, [reportYearsQuery.data]);

  const searchQuery = useTuitionFeeBenchmarkQuery(applied, applied != null);

  const resolveFilterParams = useCallback((): TuitionFeeBenchmarkFilterParams | null => {
    if (!year.trim()) return null;
    if (!programId.trim()) return null;

    const program = programsQuery.data?.find((p) => p.value === programId.trim());
    const gov = governoratesQuery.data?.find((g) => String(g.id) === governorateId.trim());
    const city = citiesQuery.data?.find((c) => String(c.id) === cityId.trim());
    const area = areasQuery.data?.find((a) => String(a.id) === areaId.trim());

    const params: TuitionFeeBenchmarkFilterParams = {
      year: year.trim(),
      gradeProgramId: programId.trim(),
      gradeProgramTitle: program?.text ?? programId.trim(),
    };

    if (gov) {
      params.governorateId = gov.id;
      params.governorateName = gov.name;
    }
    if (city) {
      params.cityId = city.id;
      params.cityName = city.name;
    }
    if (area) {
      params.areaId = area.id;
      params.areaName = area.name;
    }

    return params;
  }, [
    year,
    programId,
    governorateId,
    cityId,
    areaId,
    governoratesQuery.data,
    citiesQuery.data,
    areasQuery.data,
    programsQuery.data,
  ]);

  const handleSearch = useCallback(() => {
    if (!year.trim()) {
      message.warning('Select a year.');
      return;
    }
    if (!programId.trim()) {
      message.warning('Select a program.');
      return;
    }
    const params = resolveFilterParams();
    if (!params) return;
    setApplied(params);
    setRowFilter('all');
    setTableSearch('');
    setTablePage(1);
    setSortKey('varianceRegionalPct');
    setSortDir('desc');
  }, [year, programId, resolveFilterParams]);

  const summary = searchQuery.data?.summary;
  const rows = searchQuery.data?.rows ?? [];
  const matrix = searchQuery.data?.regionalMatrix ?? [];
  const isLoading = Boolean(applied && searchQuery.isFetching);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (rowFilter !== 'all') {
      list = list.filter((r) => r.benchmarkStatus === rowFilter);
    }
    const q = tableSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (r) =>
        r.schoolName.toLowerCase().includes(q) ||
        r.schoolCode.toLowerCase().includes(q)
    );
  }, [rows, rowFilter, tableSearch]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const m = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'schoolName') {
        return a.schoolName.localeCompare(b.schoolName, undefined, { sensitivity: 'base' }) * m;
      }
      return (a[sortKey] - b[sortKey]) * m;
    });
  }, [filteredRows, sortKey, sortDir]);

  const totalTableRows = sortedRows.length;
  const totalTablePages = Math.max(1, Math.ceil(totalTableRows / tablePageSize) || 1);

  useEffect(() => {
    setTablePage(1);
  }, [rowFilter, tableSearch, applied]);

  useEffect(() => {
    if (tablePage > totalTablePages) setTablePage(totalTablePages);
  }, [tablePage, totalTablePages]);

  const paginatedRows = useMemo(() => {
    const start = (tablePage - 1) * tablePageSize;
    return sortedRows.slice(start, start + tablePageSize);
  }, [sortedRows, tablePage, tablePageSize]);

  const emptyTableMessage = useMemo(() => {
    if (rows.length === 0) return 'No schools match these filters.';
    if (tableSearch.trim()) return 'No schools match this search.';
    if (rowFilter !== 'all') return `No schools with status "${statusLabel(rowFilter)}".`;
    return 'No schools to show.';
  }, [rows.length, tableSearch, rowFilter]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'schoolName' ? 'asc' : 'desc');
    }
  };

  const sortIndicator = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const scopeLabel = useMemo(() => {
    if (!applied) return '';
    const parts = [applied.gradeProgramTitle, applied.year];
    if (applied.governorateName) parts.push(applied.governorateName);
    if (applied.cityName) parts.push(applied.cityName);
    if (applied.areaName) parts.push(applied.areaName);
    return parts.join(' · ');
  }, [applied]);

  const regionalBenchmark = summary?.regionalBenchmarkUsd ?? summary?.nationalBenchmarkUsd ?? 0;
  const nationalBenchmark = summary?.nationalBenchmarkUsd ?? 0;

  const handleExportExcel = async () => {
    if (sortedRows.length === 0) {
      message.warning('No rows to export.');
      return;
    }
    try {
      await exportTuitionFeeBenchmarkExcel(
        sortedRows,
        tuitionFeeBenchmarkExportBasename(year, programId)
      );
      message.success('Downloaded.');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Export failed.');
    }
  };

  const handleExportPdf = async () => {
    if (sortedRows.length === 0) {
      message.warning('No rows to export.');
      return;
    }
    try {
      await exportTuitionFeeBenchmarkPdf(
        sortedRows,
        tuitionFeeBenchmarkExportBasename(year, programId),
        [`Tuition Fee Benchmark — ${scopeLabel}`]
      );
      message.success('Downloaded.');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Export failed.');
    }
  };

  const openSchoolProfile = (row: TuitionFeeBenchmarkSchoolRow) => {
    const href = schoolProfileHref({
      schoolId: row.schoolId,
      year: applied?.year ?? year,
      governorateId: row.governorateId,
    });
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6 pb-10">
      <PageHeader
        title="Tuition Fee Benchmark"
        description="Compare tuition per student by curriculum program against regional and national benchmarks for private schools."
      />

      <div className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm ring-1 ring-slate-100">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[8rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Year</label>
            <Select
              className="w-full min-w-[8rem]"
              placeholder="Year"
              value={year || undefined}
              onChange={setYear}
              options={yearOptions}
              loading={reportYearsQuery.isPending}
            />
          </div>
          <div className="min-w-[10rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Governorate</label>
            <Select
              className="w-full min-w-[10rem]"
              value={governorateId || ANY_VALUE}
              onChange={setGovernorateId}
              options={governorateOptions}
              loading={governoratesQuery.isPending}
            />
          </div>
          <div className="min-w-[10rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">District (city)</label>
            <Select
              className="w-full"
              value={cityId || ANY_VALUE}
              onChange={setCityId}
              options={cityOptions}
              loading={citiesQuery.isLoading}
              disabled={!governorateId.trim()}
            />
          </div>
          <div className="min-w-[10rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Area</label>
            <Select
              className="w-full"
              value={areaId || ANY_VALUE}
              onChange={setAreaId}
              options={areaOptions}
              loading={areasQuery.isLoading}
              disabled={!cityId.trim()}
            />
          </div>
          <div className="min-w-[12rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Program</label>
            <Select
              className="w-full"
              placeholder="Select program"
              value={programId || undefined}
              onChange={setProgramId}
              options={programOptions}
              loading={programsQuery.isPending}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <Button type="button" variant="primary" className="px-5 py-2 text-sm" onClick={handleSearch}>
            <Search className="mr-1.5 inline size-4" aria-hidden />
            Search
          </Button>
        </div>
      </div>

      {!applied ? (
        <section className="rounded-2xl border border-indigo-200/60 bg-gradient-to-br from-indigo-50/40 via-white to-slate-50/90 px-6 py-12 shadow-md ring-1 ring-indigo-100/80 sm:px-10 sm:py-14">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200/80">
              <GraduationCap className="size-7" strokeWidth={1.5} aria-hidden />
            </div>
            <h2 className="mt-6 text-xl font-bold tracking-tight text-slate-900">
              Select year and program to benchmark tuition
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Choose a reporting year and curriculum program, optionally narrow by governorate or district, then
              click Search to compare school tuition against regional and national averages.
            </p>
          </div>
        </section>
      ) : null}

      {applied && searchQuery.isError ? (
        <Alert type="error" showIcon message="Failed to load benchmark data." />
      ) : null}

      {applied && !searchQuery.isError ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <KpiCard
              label="National benchmark"
              value={isLoading ? '…' : fmtUsd(nationalBenchmark)}
              icon={DollarSign}
              detail="Student-weighted average for selected program (all governorates)"
            />
            <KpiCard
              label="Regional benchmark"
              value={
                isLoading
                  ? '…'
                  : summary?.regionalBenchmarkUsd != null
                    ? fmtUsd(summary.regionalBenchmarkUsd)
                    : fmtUsd(nationalBenchmark)
              }
              icon={MapPin}
              detail={
                applied.governorateName
                  ? `Scope: ${applied.governorateName}${applied.cityName ? ` · ${applied.cityName}` : ''}`
                  : 'National scope — regional equals national'
              }
            />
            <KpiCard
              label="Schools in scope"
              value={isLoading ? '…' : fmtInt(summary?.schoolCount ?? 0)}
              icon={Users}
            />
            <KpiCard
              label="Above regional"
              value={isLoading ? '…' : fmtInt(summary?.aboveRegionalCount ?? 0)}
              icon={TrendingUp}
              detail={`More than ${NEAR_REGIONAL_THRESHOLD_PCT}% above regional avg`}
            />
            <KpiCard
              label="Below regional"
              value={isLoading ? '…' : fmtInt(summary?.belowRegionalCount ?? 0)}
              icon={ArrowDown}
              detail={`More than ${NEAR_REGIONAL_THRESHOLD_PCT}% below regional avg`}
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-600">
              <span className="font-medium text-slate-800">{scopeLabel}</span>
              {summary?.nearRegionalCount ? (
                <span className="ml-2 text-slate-500">
                  · {summary.nearRegionalCount} within ±{NEAR_REGIONAL_THRESHOLD_PCT}% of regional
                </span>
              ) : null}
            </p>
            <Segmented
              value={viewMode}
              onChange={(v) => setViewMode(v as TuitionFeeBenchmarkViewMode)}
              options={[
                { label: 'Chart', value: 'chart' },
                { label: 'Table', value: 'table' },
                { label: 'Regional matrix', value: 'matrix' },
              ]}
            />
          </div>

          {rows.length === 0 && !isLoading ? (
            <Alert
              type="info"
              showIcon
              message="No schools match these filters."
              description="Try a different program, broaden the region, or clear district filters."
            />
          ) : null}

          {viewMode === 'chart' && rows.length > 0 ? (
            <section className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm ring-1 ring-slate-100">
              <h3 className="mb-4 text-sm font-semibold text-slate-800">Tuition vs benchmarks</h3>
              <BenchmarkChart
                rows={rows}
                regionalBenchmark={regionalBenchmark}
                nationalBenchmark={nationalBenchmark}
                loading={isLoading}
              />
              <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-600">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block size-3 rounded-sm bg-[#d97706]" aria-hidden /> Above regional
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block size-3 rounded-sm bg-[#059669]" aria-hidden /> Below regional
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block size-3 rounded-sm bg-[#4f46e5]" aria-hidden /> Near regional
                </span>
              </div>
            </section>
          ) : null}

          {viewMode === 'table' ? (
            <section className="rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-100">
              <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200/80 px-4 py-3">
                <div className="flex flex-wrap items-end gap-3">
                  <Segmented
                    size="small"
                    value={rowFilter}
                    onChange={(v) => setRowFilter(v as TuitionFeeBenchmarkRowFilter)}
                    options={[
                      { label: 'All', value: 'all' },
                      { label: 'Above', value: 'above' },
                      { label: 'Below', value: 'below' },
                      { label: 'Near', value: 'near' },
                    ]}
                  />
                  <div>
                    <label htmlFor="tfb-table-search" className="mb-1 block text-xs font-semibold text-slate-700">
                      Search schools
                    </label>
                    <Input
                      id="tfb-table-search"
                      allowClear
                      prefix={<Search className="size-4 text-slate-400" aria-hidden />}
                      placeholder="Name or code"
                      value={tableSearch}
                      onChange={(e) => setTableSearch(e.target.value)}
                      className="w-full min-w-[14rem] sm:w-56"
                      aria-label="Search schools by name or code"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pb-0.5">
                  <Button type="button" variant="secondary" className="text-xs" onClick={handleExportExcel}>
                    <FileSpreadsheet className="mr-1 inline size-3.5" aria-hidden />
                    Excel
                  </Button>
                  <Button type="button" variant="secondary" className="text-xs" onClick={handleExportPdf}>
                    <FileText className="mr-1 inline size-3.5" aria-hidden />
                    PDF
                  </Button>
                </div>
              </div>

              {isLoading ? (
                <TableSkeleton />
              ) : sortedRows.length === 0 ? (
                <p className="p-6 text-center text-sm text-slate-500">{emptyTableMessage}</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                          <th className="px-4 py-3">
                            <button type="button" className="hover:text-indigo-700" onClick={() => toggleSort('schoolName')}>
                              School{sortIndicator('schoolName')}
                            </button>
                          </th>
                          <th className="px-3 py-3">Code</th>
                          <th className="px-3 py-3">Governorate</th>
                          <th className="px-3 py-3">District</th>
                          <th className="px-3 py-3">
                            <button type="button" className="hover:text-indigo-700" onClick={() => toggleSort('studentsInGrade')}>
                              Students{sortIndicator('studentsInGrade')}
                            </button>
                          </th>
                          <th className="px-3 py-3">
                            <button type="button" className="hover:text-indigo-700" onClick={() => toggleSort('tuitionPerStudentUsd')}>
                              Tuition{sortIndicator('tuitionPerStudentUsd')}
                            </button>
                          </th>
                          <th className="px-3 py-3">Regional avg</th>
                          <th className="px-3 py-3">National avg</th>
                          <th className="px-3 py-3">
                            <button type="button" className="hover:text-indigo-700" onClick={() => toggleSort('varianceRegionalPct')}>
                              vs Regional{sortIndicator('varianceRegionalPct')}
                            </button>
                          </th>
                          <th className="px-3 py-3">
                            <button type="button" className="hover:text-indigo-700" onClick={() => toggleSort('varianceNationalPct')}>
                              vs National{sortIndicator('varianceNationalPct')}
                            </button>
                          </th>
                          <th className="px-3 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRows.map((row) => (
                          <tr key={row.schoolId} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                            <td className="max-w-[14rem] px-4 py-2.5">
                              <button
                                type="button"
                                onClick={() => openSchoolProfile(row)}
                                className="truncate text-left font-medium text-indigo-700 hover:underline"
                                title={row.schoolName}
                              >
                                {row.schoolName}
                              </button>
                            </td>
                            <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{row.schoolCode}</td>
                            <td className="px-3 py-2.5 text-slate-700">{row.governorateName}</td>
                            <td className="px-3 py-2.5 text-slate-600">
                              {row.cityName}
                              {row.areaName ? ` · ${row.areaName}` : ''}
                            </td>
                            <td className="px-3 py-2.5 font-mono tabular-nums">{fmtInt(row.studentsInGrade)}</td>
                            <td className="px-3 py-2.5 font-mono tabular-nums font-medium">{fmtUsd(row.tuitionPerStudentUsd)}</td>
                            <td className="px-3 py-2.5 font-mono tabular-nums text-slate-600">{fmtUsd(row.regionalBenchmarkUsd)}</td>
                            <td className="px-3 py-2.5 font-mono tabular-nums text-slate-600">{fmtUsd(row.nationalBenchmarkUsd)}</td>
                            <td className="px-3 py-2.5 font-mono tabular-nums">{fmtPct(row.varianceRegionalPct)}</td>
                            <td className="px-3 py-2.5 font-mono tabular-nums">{fmtPct(row.varianceNationalPct)}</td>
                            <td className="px-3 py-2.5">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${statusBadgeClass(row.benchmarkStatus)}`}
                              >
                                {statusLabel(row.benchmarkStatus)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs font-medium tabular-nums text-slate-600">
                      Showing{' '}
                      <span className="font-semibold text-slate-900">
                        {(tablePage - 1) * tablePageSize + 1}-
                        {Math.min(tablePage * tablePageSize, totalTableRows)}
                      </span>{' '}
                      of <span className="font-semibold text-slate-900">{totalTableRows}</span>
                      {tableSearch.trim() || rowFilter !== 'all' ? ' (filtered)' : ''}
                    </p>
                    <Pagination
                      size="small"
                      current={tablePage}
                      pageSize={tablePageSize}
                      total={totalTableRows}
                      showSizeChanger
                      pageSizeOptions={[...TABLE_PAGE_SIZE_OPTIONS]}
                      onChange={(p, ps) => {
                        const nextSize = ps ?? tablePageSize;
                        if (nextSize !== tablePageSize) {
                          setTablePageSize(nextSize);
                          setTablePage(1);
                        } else {
                          setTablePage(p);
                        }
                      }}
                    />
                  </div>
                </>
              )}
            </section>
          ) : null}

          {viewMode === 'matrix' ? (
            <section className="space-y-3">
              <p className="text-sm text-slate-600">
                Average tuition by region and program vs national benchmark. Cell color reflects variance from
                national average.
              </p>
              <RegionalMatrixTable
                cells={matrix}
                selectedGradeId={applied.gradeProgramId}
                loading={isLoading}
              />
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
